/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View } from '@components/atoms'
import { Seo, Footer } from '@components/systems'
import { mono, monoShared } from '@components/systems/monolith/monolith.tokens'
import { buildOrganizationJsonLd } from '@utils'

import benchImg from '@public/images/monolith/workbench.jpg'

/**
 * About — Monolith (2026-09-09 기획 확정, SITEMAP_PLAN §5·§9·§10).
 * 구 디자인은 `pages/home/About.page.tsx` 에 보관.
 *
 * 구성: 00 히어로(이름 이야기) → 실작업대 사진 → 01 Story → 02 Vision → 03 Mission → 04 Values.
 *
 * 카피 규칙
 * - Vision(`Elevate Your Value`) 과 Mission 원문은 회사가 확정한 정체성 문구다. **수정하지 않는다.**
 *   페르소나 톤과 온도가 달라도 문장을 고치는 대신 크기를 낮춰(본문 17px) 조용히 놓는 것으로 처리했다.
 * - Story 는 PERSONA 규칙(단정문·최상급 금지·설명하지 않음) + SEO_PLAN §8. 정체성 문구의 단어
 *   (elevate·value·musicians·tech·artistry·freedom)를 쓰지 않는다. 겹치지 않되 방향이 이어지게.
 * - PLS 8 랜딩의 01 ritual / 02 lineage 와 소재가 닿아 있다. 그쪽은 제품 층위(스위처가 뒤처졌다),
 *   여기는 회사 층위(우리가 건너왔다). `laptop`·`pockets`·`twenty years` 같은 표현은 여기서 쓰지 않는다.
 * - 시즌 카피("the WAY, the TRUTH, and the LIFE")는 Home 밴드 + 푸터 두 자리 규칙이라 여기 쓰지 않는다.
 *   사자는 이름으로만 등장한다.
 * - **Values 는 스펙도 정책도 아니다.** 이 회사가 연주자를 어떻게 보는지 셋(시간 · 평등 · 자리)이다.
 *   초안에 SITEMAP_PLAN §5 의 `02 Engineering`(집착의 산물 3점 — 임피던스·디스플레이·워드마크 1.43)을
 *   넣었다가 걷어냈다(2026-09-09 사용자: "제품설명에나 들어갈법한 내용"). 그 3점은 제품 상세로 간다.
 *   비전을 다시 말하지 않고, 그 값이 **어디에 있다고 보는지**로 쪼갠다.
 * - 회사 정보(대표·사업자·주소)는 사용자 결정으로 화면에서 뺐다. 푸터 하단에 이미 있고, 구조화
 *   데이터는 `Organization` JSON-LD 가 계속 담당한다.
 *
 * 사진: `public/images/monolith/workbench.jpg` — 실제 작업대 원본(IMG_9349). 파일 자체를 그레이스케일로
 * 구워서 넣었다(스톡이 아니라 자사 사진이라 CSS filter 대신 원본 변환). 컬러 마스터는
 * `claudeFiles/judahsWeb/webImage/workshop/workbench.jpg`.
 */
const MonolithAboutPage = () => {
	const jsonLd = React.useMemo(() => buildOrganizationJsonLd(), [])

	const valueList: { no: string; title: string; body: string }[] = [
		{
			no: '01',
			title: 'The hours nobody applauds are still the work.',
			body: 'Most of a player’s day is spent setting up and waiting. Time taken back from that is the plainest thing we can hand over.',
		},
		{
			no: '02',
			title: 'The size of the room does not set the worth.',
			body: 'A player in front of thirty people and a player in front of thirty thousand get the same unit. We have never found a reason to keep two standards.',
		},
		{
			no: '03',
			title: 'The name on the stage is not ours.',
			body: 'We build the part that stands behind the player and stays quiet. The applause is not addressed to us, and it should not be.',
		},
	]

	return (
		<View css={style}>
			<Seo jsonLd={jsonLd} />

			<View className="mnWrap">
				<View className="mnPageHeader">
					<h1 className="mnPageTitle">About</h1>
					<p className="mnAboutLede">
						We chose the name first. Then we built something that could carry it.
					</p>
				</View>

				<View className="mnBenchView">
					<img
						src={benchImg}
						alt="The Judahs workbench: a control board under assembly, a soldering station and a Koinos PLS 8 with its display on"
						className="mnBenchImage"
					/>
				</View>

				{/* 01 Story */}
				<View className="mnBand mnRow">
					<span className="mnKicker">Story</span>
					<View className="mnRowBody">
						<p className="mnBody">
							The lion was decided before the first enclosure existed. A name settles what
							you are permitted to make afterwards, and it settles it early.
						</p>
						<p className="mnBody">
							What the name was waiting for was not the part anyone photographs. The world
							had moved to the touch of a finger, and one corner of it had not. A player
							who wanted to change a single setting still had to connect the unit to a
							computer and sit down, and everyone had learned to live with that. We came
							from the side of the work where this was settled long ago. We did not find
							it acceptable, and we did not find it hard.
						</p>
						<p className="mnBody">
							A name like that sets a floor. It cannot be put on something finished in a
							hurry, or on something we are not prepared to be read through.
						</p>
						<p className="mnBody">
							It has cost us time. It has decided more about what we do not ship than
							about what we do.
						</p>
						<p className="mnBody mnBodyClose">
							“Anyone who ends up in front of one has to stand in that lion’s presence.”
						</p>
					</View>
				</View>

				{/* 02 Vision */}
				<View className="mnBand mnRow mnVision">
					<span className="mnKicker">Vision</span>
					<View className="mnRowBody">
						<p className="mnVisionLine">Elevate Your Value</p>
					</View>
				</View>

				{/* 03 Mission */}
				<View className="mnBand mnRow">
					<span className="mnKicker">Mission</span>
					<View className="mnRowBody">
						<p className="mnBody">
							We are a brand that elevates the value of musicians who change the world
							through music — uniting tech and artistry without limits, delivering
							powerful, accessible solutions that help global artists create with greater
							freedom.
						</p>
					</View>
				</View>

				{/* 04 Values */}
				<View className="mnBand mnValues">
					<span className="mnKicker mnValuesKicker">Values</span>
					<View className="mnIndex">
						{valueList.map(aValue => (
							<View key={aValue.no} className="mnIndexRow">
								<View className="mnIndexHead">
									<span className="mnIndexNo">{aValue.no}</span>
									<h2 className="mnIndexTitle">{aValue.title}</h2>
								</View>
								<View className="mnIndexBody">
									<p className="mnBody">{aValue.body}</p>
								</View>
							</View>
						))}
					</View>
				</View>
			</View>

			<Footer />
		</View>
	)
}

export default MonolithAboutPage

const style = css({
	...monoShared,
	position: 'relative',
	width: '100%',

	/**
	 * 제목 글자 규격은 다른 페이지(Shop · Support · Forum)의 `.mnPageTitle` 과 같다.
	 * 배치만 다르다 — 4:8 좌우가 아니라 제목 아래에 헤드라인을 쌓는다(2026-09-09 사용자 지시).
	 * 헤드라인은 공용 `.mnPageLede`(19px 300 dim) 대신 DESIGN_SYSTEM §3 의 "리드 문장" 스케일.
	 * 이 페이지에서 가장 강한 문장이라 19px 로 내리면 부제로 읽힌다.
	 */
	'.mnPageHeader': {
		padding: 'clamp(64px, 8.3vw, 120px) 0 clamp(48px, 6.25vw, 90px)',

		'.mnPageTitle': {
			fontSize: 'clamp(56px, 7.6vw, 110px)',
			lineHeight: 1,
			fontWeight: 100,
			letterSpacing: '-0.01em',
			color: mono.ink,
		},

		'.mnAboutLede': {
			marginTop: 'clamp(28px, 3vw, 44px)',
			fontSize: 'clamp(24px, 2.5vw, 36px)',
			lineHeight: 1.35,
			fontWeight: 200,
			letterSpacing: '-0.005em',
			color: mono.ink,
			maxWidth: 720,
		},
	},

	'.mnBenchView': {
		width: '100%',

		'.mnBenchImage': {
			display: 'block',
			width: '100%',
			aspectRatio: '3 / 2',
			objectFit: 'cover',
			objectPosition: 'center 72%',

			'@media (max-width: 700px)': {
				aspectRatio: '4 / 3',
			},
		},
	},

	// 객체 리터럴에서 같은 키는 뒤가 통째로 이기므로 borderTop 도 같이 다시 적는다.
	'.mnBand': {
		borderTop: `1px solid ${mono.line}`,
		padding: 'clamp(64px, 8vw, 110px) 0',
	},

	'.mnRow': {
		display: 'grid',
		gridTemplateColumns: 'minmax(0, 4fr) minmax(0, 8fr)',
		gap: 48,

		'.mnKicker': {
			display: 'block',
			paddingTop: 6,
		},

		'@media (max-width: 900px)': {
			gridTemplateColumns: '1fr',
			gap: 24,
		},
	},

	'.mnRowBody': {
		maxWidth: 660,

		'> p + p': {
			marginTop: 26,
		},
	},

	'.mnBody': {
		fontSize: 17,
		lineHeight: 1.75,
		fontWeight: 300,
		color: mono.dim,

		/** Story 의 마지막 줄. 크기는 본문 그대로 두고 웨이트와 큰따옴표로만 들어올린다. */
		'&.mnBodyClose': {
			fontWeight: 600,
			color: mono.ink,
		},
	},

	/** Story · Mission 과 같은 4:8 행에 얹는다 — 키커 열이 세 밴드에서 같은 자리에 선다. */
	'.mnVision': {
		'.mnVisionLine': {
			fontSize: 'clamp(32px, 3.6vw, 52px)',
			lineHeight: 1.2,
			fontWeight: 100,
			letterSpacing: '-0.005em',
			color: mono.ink,
		},
	},

	'.mnValues': {
		paddingBottom: 'clamp(24px, 3vw, 40px)',

		'.mnValuesKicker': {
			display: 'block',
			marginBottom: 'clamp(36px, 4vw, 56px)',
		},
	},

	'.mnIndex': {
		borderTop: `1px solid ${mono.line}`,
	},

	'.mnIndexRow': {
		display: 'grid',
		gridTemplateColumns: 'minmax(0, 4fr) minmax(0, 8fr)',
		gap: 48,
		padding: 'clamp(36px, 3.6vw, 52px) 0',
		borderBottom: `1px solid ${mono.line}`,

		'@media (max-width: 900px)': {
			gridTemplateColumns: '1fr',
			gap: 18,
		},
	},

	'.mnIndexHead': {
		display: 'flex',
		alignItems: 'baseline',
		gap: 24,

		'.mnIndexNo': {
			fontSize: 12,
			letterSpacing: '.22em',
			color: mono.faint,
			fontWeight: 500,
			fontVariantNumeric: 'tabular-nums',
			minWidth: 28,
		},
		'.mnIndexTitle': {
			fontSize: 'clamp(24px, 2.2vw, 32px)',
			lineHeight: 1.25,
			fontWeight: 300,
			color: mono.ink,
		},
	},

	'.mnIndexBody': {
		maxWidth: 620,
	},
})
